import React, { useState, useEffect, useRef } from 'react';
import { Container, Form, Row, Col, Alert } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import '../styles/checkout.css';
import states from '../components/Utils/states.js';
import { getFedexHubListByZip, createOrderWithQuote } from '../services/api.js';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import useNotification from '../hooks/useNotification';
import {
  validateField,
  validateAddress,
  normalizeText,
  formatZip,
  formatPhone,
  extractPhoneDigits,
  AddressRules,
} from '../utils/addressSchema';
import { trackCheckoutEvent } from '../utils/telemetry';
import WeatherTrustBlock from '../components/common/WeatherTrustBlock';
import ShippingTemperatureNotice from '../components/common/ShippingTemperatureNotice';
import { scrollIntoViewMotionSafe } from '../utils/scrollHelpers';
import { isPlantOnlyCart } from '../constants/shippingRules';
import { PLANTS_SHIP_WITH_ANIMALS_MESSAGE } from '../constants/shippingRules';

const CheckoutAddressPage = () => {
  const navigate = useNavigate();
  const { cartId, cartItems = [], isLoading: cartLoading,
          ensureBackendCart, refreshCart } = useCart();
  const { isAuthenticated, isEmailVerified, user, refreshUserProfile, hydrated } = useAuth();
  const { showNotification } = useNotification();

  const [shippingAddress, setShippingAddress] = useState({
    first_name: '',
    last_name: '',
    company: '',
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    zip: '',
    phone: '',
  });

  const [shipToHub, setShipToHub] = useState(true);
  const [selectedHub, setSelectedHub] = useState('');
  const [hubZip, setHubZip] = useState('');
  const [showHubOptions, setShowHubOptions] = useState(false);
  const [hubOptions, setHubOptions] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // The native `required` check blocks submit before handleSubmit ever
  // runs, and its bubble is easy to miss (and invisible to automation) —
  // so "Proceed to Payment" looked dead with an empty hub ZIP. onInvalid
  // mirrors the browser's refusal into the form's own styling.
  const [hubZipError, setHubZipError] = useState(null);
  // Set when a submit is REFUSED locally (plant-only cart). A blocked
  // submit must never look like a dead button, so this renders the
  // shared sentence right above the CTA the customer just pressed.
  const [blockedMessage, setBlockedMessage] = useState(null);
  // refreshCart is redefined every render; a ref keeps the guard effect off
  // its identity (listing it as a dep would refetch in a loop).
  const refreshCartRef = useRef(refreshCart);
  refreshCartRef.current = refreshCart;
  // The arrival bounce decides ONCE per mount. Without this it re-ran on
  // every `cartItems` identity change — and verifyPlantOnly() itself calls
  // refreshCart, which setCartItems(new array). So a submit that confirmed
  // plant-only and showed the stay-here message immediately re-triggered
  // the arrival effect, which navigated away anyway and fired a second
  // notification: the refuse-and-stay contract overridden by its own
  // verification (review 2026-09-05).
  const bounceDecidedRef = useRef(false);
  // Once the customer has pressed the button, the SUBMIT path owns the
  // outcome. An effect must not yank the page out from under it.
  const submitAttemptedRef = useRef(false);
  const plantsNotifiedRef = useRef(false);

  /** One plants-ship-with-animals toast per situation, not per code path. */
  const notifyPlantsOnce = () => {
    if (plantsNotifiedRef.current) return;
    plantsNotifiedRef.current = true;
    showNotification(PLANTS_SHIP_WITH_ANIMALS_MESSAGE, 'warning');
  };
  const firstInvalidRef = useRef(null);
  // Cowork run 3: one click produced TWO POSTs to the guarded
  // order-creation endpoint — setIsSubmitting is async, so a second
  // dispatch in the same tick beats the disabled re-render. This ref
  // blocks re-entry synchronously (PaymentForm's isProcessingRef pattern).
  const isSubmittingRef = useRef(false);
  const [isLoadingHubs, setIsLoadingHubs] = useState(false);

  const [billingSameAsShipping, setBillingSameAsShipping] = useState(true);

  // Validation errors for billing and shipping addresses
  const [billingErrors, setBillingErrors] = useState({});
  const [shippingErrors, setShippingErrors] = useState({});

  // Clear any modal scroll locks on mount and ensure page is scrollable
  useEffect(() => {
    // Remove all possible modal class names that could prevent scrolling
    document.body.classList.remove(
      'modal-open',
      'no-scroll',
      'ff-modal-open',
      'ff-scroll-lock'
    );

    // Add checkout-page class for specific scroll fixes
    document.body.classList.add('checkout-page');

    // Reset all overflow styles
    document.documentElement.style.overflow = '';
    document.body.style.overflow = '';
    document.documentElement.style.paddingRight = '';
    document.body.style.paddingRight = '';

    // Force page to be scrollable
    document.body.style.position = '';
    document.documentElement.style.position = '';

    // Track checkout started event
    trackCheckoutEvent('checkout_started', null, {
      item_count: cartItems.length,
      cart_id: cartId,
    });

    // Cleanup on unmount
    return () => {
      document.body.classList.remove('checkout-page');
    };
  }, []);

  // Check email verification on component mount
  useEffect(() => {
    // Wait for AuthContext to finish hydrating
    if (!hydrated) {
      return;
    }
    
    if (isAuthenticated && !isEmailVerified) {
      // Safety refresh - user might have verified email in another tab
      refreshUserProfile().then((refreshedData) => {
        if (refreshedData && !refreshedData.is_email_verified) {
          // Still not verified after refresh
          showNotification('Oops — just one more step! Please verify your email before completing checkout.', 'warning');
          navigate('/email-confirmation-sent?from=checkout&redirect=/checkout/address');
        }
      }).catch((error) => {
        // Fallback to showing verification screen
        showNotification('Oops — just one more step! Please verify your email before completing checkout.', 'warning');
        navigate('/email-confirmation-sent?from=checkout&redirect=/checkout/address');
      });
      return;
    }
  }, [isAuthenticated, isEmailVerified, user, hydrated, navigate, showNotification, refreshUserProfile]);

  const [billingAddress, setBillingAddress] = useState({
    first_name: '',
    last_name: '',
    company: '',
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    zip: '',
    phone: '',
  });

  const handleChange = (e, sectionSetter, sectionData) => {
    const { name, value } = e.target;
    let processedValue = value;

    // Auto-format ZIP code (5 digits, strip non-digits)
    if (name === 'zip') {
      processedValue = formatZip(value);
    }

    // Auto-format phone (visual display only, we'll extract digits on submit)
    if (name === 'phone') {
      processedValue = formatPhone(value);
    }

    sectionSetter({ ...sectionData, [name]: processedValue });
  };

  // Validate field on blur
  const handleBlur = (fieldName, value, errorsSetter, isBilling = true) => {
    // Map form field names to validation rule keys
    const fieldMapping = {
      first_name: 'firstName',
      last_name: 'lastName',
      company: 'company',
      address_line1: 'addressLine1',
      address_line2: 'addressLine2',
      city: 'city',
      state: 'state',
      zip: 'zip',
      phone: 'phone',
    };

    const ruleKey = fieldMapping[fieldName];
    if (!ruleKey) return;

    const result = validateField(ruleKey, value, AddressRules);

    errorsSetter(prev => ({
      ...prev,
      [fieldName]: result.valid ? null : result.error,
    }));
  };

  const handleFindHubs = async () => {
    try {
      setIsLoadingHubs(true);
      const hubs = await getFedexHubListByZip(hubZip);
      setHubOptions(hubs || []);
      setShowHubOptions(true);
    } catch (err) {
      showNotification('Failed to fetch FedEx hub locations. Please try again.', 'error');
      setHubOptions([]);
    } finally {
      setIsLoadingHubs(false);
    }
  };

  // #27: take the customer to the first invalid field. Since #65 made
  // the CTA reachable from anywhere in the form, submitting from the top
  // otherwise shows a toast over a screen with no visible error on it.
  // Walks the section's fields in DOM order, not the error object's key
  // order, so "first" means first ON SCREEN.
  const scrollToFirstError = (section, errors) => {
    const errored = new Set(
      Object.keys(errors || {}).filter((key) => errors[key]));
    if (!errored.size) return;
    const root = document.querySelector(`[data-address-section="${section}"]`);
    if (!root) return;
    const field = Array.from(root.querySelectorAll('[name]'))
      .find((el) => errored.has(el.getAttribute('name')));
    if (!field) return;
    scrollIntoViewMotionSafe(field, { block: 'center' });
    // focus after the scroll is under way — focusing first yanks the
    // viewport instantly and undoes the smooth scroll
    window.setTimeout(() => {
      try { field.focus({ preventScroll: true }); } catch (_) { /* older browsers */ }
    }, 350);
  };

  /**
   * The browser's own validation refusal, made visible.
   *
   * Cowork's beta run reported "Proceed to Payment does nothing" on an
   * all-plant cart. It wasn't the 422 (that surfaces correctly on a
   * valid form) — the form never submitted: "Ship to FedEx Hub" is the
   * DEFAULT delivery method and its ZIP field is `required`, so an empty
   * one cancels submit before handleSubmit runs. The native bubble is
   * transient and invisible to automation, so the button reads as dead.
   *
   * `invalid` fires per control; the ref keeps only the FIRST one, which
   * is the one the browser would have focused.
   */
  /**
   * Re-fetch the cart and answer "is it REALLY plant-only?".
   *
   * Returns true / false on evidence, and NULL when we could not get
   * any — refreshCart returns null both on a network error and when a
   * concurrent fetch is already running.
   *
   * A/B on the live build (2026-09-05): a browser context that had once
   * held a plant-only cart kept that snapshot in context state /
   * localStorage after the cart was changed server-side to gecko +
   * package. The guard read the CACHE, refused, and the customer got a
   * dead button on a perfectly shippable order. So: never refuse on
   * cached data. The server cart is the authority, and where we cannot
   * reach it we defer to the backend's own 422 rather than guessing.
   */
  const verifyPlantOnly = async () => {
    try {
      const verified = await refreshCartRef.current?.();
      const freshItems = Array.isArray(verified?.items) ? verified.items : null;
      if (!freshItems) return null;
      return isPlantOnlyCart(freshItems);
    } catch (err) {
      console.warn('[CHECKOUT] cart verification failed:', err?.message || err);
      return null;
    }
  };

  // EmailVerified.js navigates straight here after verification, and the
  // URL is reachable directly — both bypass the cart's advisory, so the
  // rule is enforced on arrival too: bounce to /cart, where the sentence
  // and the disabled button explain it.
  //
  // MUST WAIT FOR THE CART (live probe on 6cf50d9d): the first version
  // ran on mount against a cart that had not loaded yet. An empty list is
  // not plant-only, so it never bounced — and because the effect only
  // re-ran on `cartItems`, a cart arriving with a stable-empty identity
  // could miss the re-check entirely. An UNLOADED cart is not evidence of
  // anything: judge it only once loading is done, and never bounce an
  // empty cart (that is the "your cart is empty" story, not this one).
  useEffect(() => {
    // ONE decision per mount, and never after a submit attempt.
    if (bounceDecidedRef.current || submitAttemptedRef.current) return;
    if (cartLoading) return;
    if (!Array.isArray(cartItems) || cartItems.length === 0) return;

    // Claim the decision BEFORE any await: verifyPlantOnly() refreshes the
    // cart, and that state write re-runs this effect.
    bounceDecidedRef.current = true;

    if (!isPlantOnlyCart(cartItems)) {
      setBlockedMessage(null);        // a mixed cart clears any stale refusal
      plantsNotifiedRef.current = false;
      return;
    }
    // The CACHED cart looks plant-only — confirm against the server
    // before sending the customer away. A stale snapshot must never
    // bounce someone off a cart that is actually mixed.
    let cancelled = false;
    (async () => {
      const reallyPlantOnly = await verifyPlantOnly();
      // A submit may have started while we were verifying; it wins.
      if (cancelled || submitAttemptedRef.current) return;
      if (reallyPlantOnly !== true) return;
      console.warn('[CHECKOUT] Plant-only cart on the address page — '
        + 'returning to /cart');
      notifyPlantsOnce();
      navigate('/cart', { replace: true });
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartLoading, cartItems, navigate]);

  const handleInvalid = (e) => {
    const el = e.target;
    // Mark EVERY refused control, not only the first — the browser stops
    // at the first, but the customer should see all of them (adv-review).
    // These go into the form's OWN error maps, which already drive
    // isInvalid + Feedback on each field, so nothing new has to render.
    const section = el?.closest?.('[data-address-section]')
      ?.getAttribute('data-address-section');
    if (el?.name && section) {
      const message = el.validationMessage || 'This field is required.';
      const setter = section === 'billing' ? setBillingErrors : setShippingErrors;
      setter((prev) => ({ ...prev, [el.name]: message }));
    }
    if (el?.name === 'hub_zip') {
      setHubZipError(
        'Enter a ZIP code and choose a hub, or switch to "Ship to My Address".'
      );
    }
    // Only the FIRST invalid control gets focus + scroll — that is the one
    // the browser would have focused, and stealing focus per control would
    // land on the last.
    if (firstInvalidRef.current) return;
    firstInvalidRef.current = el;
    setTimeout(() => {
      try {
        el?.focus?.({ preventScroll: true });
        // Centred, like scrollToFirstError above: the default `start`
        // parks the field under the sticky navbar (R5 B1 review).
        scrollIntoViewMotionSafe(el, { block: 'center' });
      } catch (err) {
        console.warn('[CHECKOUT] could not focus invalid field:', err?.message);
      }
      firstInvalidRef.current = null;
    }, 0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmittingRef.current) return;
    // From here the submit path owns the plant-only outcome; the arrival
    // bounce must not navigate away behind it.
    submitAttemptedRef.current = true;

    // Additional check for email verification before submitting
    if (isAuthenticated && !isEmailVerified) {
      showNotification('Oops — just one more step! Please verify your email before placing an order.', 'warning');
      navigate('/email-confirmation-sent?from=checkout&redirect=/checkout/address');
      return;
    }

    // ✅ Check for cart items instead of just cartId
    if (!cartId && (!cartItems || cartItems.length === 0)) {
      setBlockedMessage('No cart found. Please add items to your cart first.');
      showNotification('No cart found. Please add items to your cart first.', 'error');
      return;
    }

    // Validate billing address
    const billingValidation = validateAddress(billingAddress);
    if (!billingValidation.valid) {
      setBillingErrors(billingValidation.errors);
      showNotification('Please correct the errors in your billing address.', 'error');
      scrollToFirstError('billing', billingValidation.errors);
      return;
    }

    // Validate shipping address if separate from billing
    if (!shipToHub && !billingSameAsShipping) {
      const shippingValidation = validateAddress(shippingAddress);
      if (!shippingValidation.valid) {
        setShippingErrors(shippingValidation.errors);
        showNotification('Please correct the errors in your shipping address.', 'error');
        scrollToFirstError('shipping', shippingValidation.errors);
        return;
      }
    }

    isSubmittingRef.current = true;
    setBlockedMessage(null);
    setIsSubmitting(true);

    try {
      // Ensure backend cart exists before creating order
      console.log('[CHECKOUT] ensuring cartId...');
      const ensuredId = await ensureBackendCart();
      console.log('[CHECKOUT] ensure result id=', ensuredId);

      // If still no ID, try fetching cart directly as fallback
      let finalCartId = ensuredId;
      if (!finalCartId && cartItems.length > 0) {
        console.log('[CHECKOUT] fallback: fetching cart directly...');
        const fresh = await refreshCart();
        finalCartId = fresh?.id ?? null;
        console.log('[CHECKOUT] fallback fetch id=', finalCartId);

        if (!finalCartId) {
          showNotification('Unable to prepare cart for checkout. Please refresh and try again.', 'error');
          isSubmittingRef.current = false;
          setIsSubmitting(false);
          return;
        }
      }

      // Belt and braces behind the bounce, ON FRESH EVIDENCE ONLY. The
      // cached cart is checked first purely to avoid a needless fetch on
      // every checkout: only a cart that LOOKS plant-only is verified,
      // and only a verified plant-only cart is refused. An unverifiable
      // cart proceeds — the backend's 422 is the real gate, and its
      // message surfaces in the catch below.
      if (isPlantOnlyCart(cartItems)) {
        const reallyPlantOnly = await verifyPlantOnly();
        if (reallyPlantOnly === true) {
          console.warn('[CHECKOUT] Plant-only cart blocked at address submit');
          setBlockedMessage(PLANTS_SHIP_WITH_ANIMALS_MESSAGE);
          notifyPlantsOnce();
          return;
        }
        console.warn('[CHECKOUT] cached cart looked plant-only but the '
          + 'server cart does not agree — proceeding');
      }

      console.log('[CHECKOUT] Proceeding with cartId:', finalCartId);

      // Determine the destination address based on shipping method
      let destinationData;
      let dropOffType = 'REGULAR_PICKUP';
      let residential = true;

      if (shipToHub && selectedHub) {
        // Find the selected hub data
        const hubData = hubOptions.find(hub => 
          (hub.locationId || hubOptions.indexOf(hub).toString()) === selectedHub
        );
        
        if (hubData?.contactAndAddress?.address) {
          const addr = hubData.contactAndAddress.address;
          destinationData = {
            first_name: billingAddress.first_name,
            last_name: billingAddress.last_name,
            company: billingAddress.company || '',
            city: addr.city || '',
            state: addr.stateOrProvinceCode || '',
            zip: addr.postalCode || '',
            address_line_1: addr.streetLines?.[0] || '',
            address_line_2: addr.streetLines?.[1] || '',
            phone: billingAddress.phone
          };
          dropOffType = 'HOLD_AT_LOCATION';
          residential = false;
        } else {
          throw new Error('Selected hub data is incomplete');
        }
      } else {
        // Ship to home address
        const shippingAddr = billingSameAsShipping ? billingAddress : shippingAddress;
        destinationData = {
          first_name: shippingAddr.first_name,
          last_name: shippingAddr.last_name,
          company: shippingAddr.company || '',
          city: shippingAddr.city,
          state: shippingAddr.state,
          zip: shippingAddr.zip,
          address_line_1: shippingAddr.address_line1,
          address_line_2: shippingAddr.address_line2 || '',
          phone: shippingAddr.phone
        };
        residential = true;
      }

      const payload = {
        cart_id: finalCartId || null, // Use ensured cart ID
        session_key: null, // Will be handled by backend if needed
        first_name: destinationData.first_name,
        last_name: destinationData.last_name,
        company: destinationData.company,
        zip: destinationData.zip,
        city: destinationData.city,
        state: destinationData.state,
        address_line1: destinationData.address_line_1,
        address_line2: destinationData.address_line_2,
        contact_phone: destinationData.phone,
        drop_off_type: dropOffType,
        residential: residential
      };

      // Add hub_id for hub pickup orders
      if (shipToHub && selectedHub) {
        const hubData = hubOptions.find(hub =>
          (hub.locationId || hubOptions.indexOf(hub).toString()) === selectedHub
        );
        if (hubData?.locationId) {
          payload.hub_id = hubData.locationId;
        }
      }

      // CRITICAL FIX: Include manually applied promo code from localStorage
      // This ensures promo codes persist through cart → checkout transition
      try {
        const appliedPromoCode = localStorage.getItem('appliedPromoCode');
        if (appliedPromoCode) {
          payload.promo_code = appliedPromoCode;
          console.log('[CHECKOUT] Including promo code in order creation:', appliedPromoCode);
        }
      } catch (error) {
        console.error('[CHECKOUT] Failed to read promo code from localStorage:', error);
      }

      const result = await createOrderWithQuote(payload);
      showNotification('Order created successfully!', 'success');

      // Track checkout address submitted event
      trackCheckoutEvent('checkout_address_submitted', result.order.id, {
        ship_to_hub: shipToHub,
        item_count: cartItems.length,
      });

      // Persist order data + addresses in sessionStorage as backup (survives page refresh)
      const orderData = {
        orderId: result.order.id,
        orderSummary: result.order,
        quote: result.quote,
        addresses: {
          billing: billingAddress,
          shipping: destinationData,
          shipToHub: shipToHub
        }
      };
      sessionStorage.setItem('checkout-order-data', JSON.stringify(orderData));

      // Navigate to payment page with order details
      navigate(`/checkout/payment?orderId=${result.order.id}`, {
        state: {
          order: result.order,
          quote: result.quote,
          addresses: {
            billing: billingAddress,
            shipping: destinationData,
            shipToHub: shipToHub
          }
        }
      });
      
    } catch (error) {
      // Prefer the human-readable message (e.g. the plants-ship-with-
      // animals 422) over machine error codes. Verified working on the
      // live build 2026-09-05 — deliberately ONE message, no inline
      // duplicate.
      showNotification(
        error.response?.data?.message
          || error.response?.data?.error
          || 'Failed to create order. Please try again.',
        'error'
      );
    } finally {
      isSubmittingRef.current = false;
      setIsSubmitting(false);
    }
  };

  // If user is authenticated but email is not verified, show blocking message
  if (isAuthenticated && !isEmailVerified) {
    return (
      <Container className="py-4">
        <div className="text-center">
          <h3>Email Verification Required</h3>
          <Alert variant="warning" className="mt-4">
            <Alert.Heading>Please verify your email address</Alert.Heading>
            <p>
              You must verify your email address before placing an order. Please check your inbox for a verification email.
            </p>
            <hr />
            <p className="mb-0">
              If you didn't receive the email, you can request a new verification email from your account settings.
            </p>
          </Alert>
        </div>
      </Container>
    );
  }

  return (
    <div className="checkout-inner">
      <Container className="py-4 checkout-container">
        <Form onSubmit={handleSubmit} onInvalid={handleInvalid}>
          <h2 className="checkout-heading text-center">Checkout - Address Information</h2>
          <div>
            {/* Billing Address always shown first */}
            <div className="address-col mb-4" data-address-section="billing">
              <h5 className="checkout-subheading">Billing Address</h5>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>First Name</Form.Label>
                    <Form.Control
                      name="first_name"
                      value={billingAddress.first_name || ''}
                      onChange={(e) => handleChange(e, setBillingAddress, billingAddress)}
                      onBlur={(e) => handleBlur('first_name', e.target.value, setBillingErrors, true)}
                      isInvalid={!!billingErrors.first_name}
                      required
                    />
                    <Form.Control.Feedback type="invalid">
                      {billingErrors.first_name}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Last Name</Form.Label>
                    <Form.Control
                      name="last_name"
                      value={billingAddress.last_name || ''}
                      onChange={(e) => handleChange(e, setBillingAddress, billingAddress)}
                      onBlur={(e) => handleBlur('last_name', e.target.value, setBillingErrors, true)}
                      isInvalid={!!billingErrors.last_name}
                      required
                    />
                    <Form.Control.Feedback type="invalid">
                      {billingErrors.last_name}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>
              <Form.Group className="mb-3">
                <Form.Label>Company (Optional)</Form.Label>
                <Form.Control
                  name="company"
                  value={billingAddress.company || ''}
                  onChange={(e) => handleChange(e, setBillingAddress, billingAddress)}
                  onBlur={(e) => handleBlur('company', e.target.value, setBillingErrors, true)}
                  isInvalid={!!billingErrors.company}
                />
                <Form.Control.Feedback type="invalid">
                  {billingErrors.company}
                </Form.Control.Feedback>
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Address Line 1</Form.Label>
                <Form.Control
                  name="address_line1"
                  value={billingAddress.address_line1}
                  onChange={(e) => handleChange(e, setBillingAddress, billingAddress)}
                  onBlur={(e) => handleBlur('address_line1', e.target.value, setBillingErrors, true)}
                  isInvalid={!!billingErrors.address_line1}
                  required
                />
                <Form.Control.Feedback type="invalid">
                  {billingErrors.address_line1}
                </Form.Control.Feedback>
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Address Line 2 (Optional)</Form.Label>
                <Form.Control
                  name="address_line2"
                  value={billingAddress.address_line2}
                  onChange={(e) => handleChange(e, setBillingAddress, billingAddress)}
                  onBlur={(e) => handleBlur('address_line2', e.target.value, setBillingErrors, true)}
                  isInvalid={!!billingErrors.address_line2}
                />
                <Form.Control.Feedback type="invalid">
                  {billingErrors.address_line2}
                </Form.Control.Feedback>
              </Form.Group>
              <Row>
                <Col md={5}>
                  <Form.Group className="mb-3">
                    <Form.Label>City</Form.Label>
                    <Form.Control
                      name="city"
                      value={billingAddress.city}
                      onChange={(e) => handleChange(e, setBillingAddress, billingAddress)}
                      onBlur={(e) => handleBlur('city', e.target.value, setBillingErrors, true)}
                      isInvalid={!!billingErrors.city}
                      required
                    />
                    <Form.Control.Feedback type="invalid">
                      {billingErrors.city}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>State</Form.Label>
                    <Form.Select
                      name="state"
                      value={billingAddress.state}
                      onChange={(e) => handleChange(e, setBillingAddress, billingAddress)}
                      onBlur={(e) => handleBlur('state', e.target.value, setBillingErrors, true)}
                      isInvalid={!!billingErrors.state}
                      required
                    >
                      <option value="">Select a state</option>
                      {states.map((stateName) => (
                        <option key={stateName} value={stateName}>{stateName}</option>
                      ))}
                    </Form.Select>
                    <Form.Control.Feedback type="invalid">
                      {billingErrors.state}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group className="mb-3">
                    <Form.Label>Zip Code</Form.Label>
                    <Form.Control
                      name="zip"
                      value={billingAddress.zip}
                      onChange={(e) => handleChange(e, setBillingAddress, billingAddress)}
                      onBlur={(e) => handleBlur('zip', e.target.value, setBillingErrors, true)}
                      isInvalid={!!billingErrors.zip}
                      placeholder="64105"
                      maxLength={5}
                      required
                    />
                    <Form.Control.Feedback type="invalid">
                      {billingErrors.zip}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Phone</Form.Label>
                    <Form.Control
                      type="tel"
                      name="phone"
                      value={billingAddress.phone}
                      onChange={(e) => handleChange(e, setBillingAddress, billingAddress)}
                      onBlur={(e) => handleBlur('phone', e.target.value, setBillingErrors, true)}
                      isInvalid={!!billingErrors.phone}
                      placeholder="(816) 555-1234"
                      title="Please enter a valid phone number so the carrier can reach you if needed."
                      required
                    />
                    <Form.Control.Feedback type="invalid">
                      {billingErrors.phone}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>
            </div>

            {/* Delivery Method Selection */}
            <div className="address-col">
              <Form.Group className="mb-3">
                <Form.Label>Choose Delivery Method</Form.Label>
                <Form.Select
                  value={shipToHub ? "hub" : "home"}
                  onChange={(e) => {
                    setShipToHub(e.target.value === "hub");
                    setHubZipError(null);
                  }}
                >
                  <option value="hub">Ship to FedEx Hub (Recommended)</option>
                  <option value="home">Ship to My Address</option>
                </Form.Select>
              </Form.Group>
            </div>

            {/* Shipping Policy Notice — fix batch 42: this is the step where the
                method is being CHOSEN, so both windows are shown beside the
                choice, whichever is selected (it used to say "Residential"
                and sat inside the home-delivery branch only). */}
            <div className="address-col mb-4">
              <Alert variant="info" className="text-start">
                <ShippingTemperatureNotice lineClassName="mb-1" label="🌡️ Temperature Policy:" />
                <button
                  type="button"
                  className="ff-btn ff-btn--link p-0 align-baseline text-decoration-underline"
                  onClick={() => navigate('/shipping-policy')}
                  style={{ fontSize: 'inherit' }}
                >
                  View full shipping policy
                </button>
              </Alert>
            </div>
            {shipToHub && (
              <div className="hub-lookup-group">
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Enter ZIP Code for Hub Lookup</Form.Label>
                      <Form.Control
                        name="hub_zip"
                        value={hubZip}
                        onChange={(e) => {
                          setHubZip(e.target.value);
                          if (hubZipError) setHubZipError(null);
                        }}
                        isInvalid={!!hubZipError}
                        required
                      />
                      <Form.Control.Feedback type="invalid">
                        {hubZipError}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                  <Col md={6} className="d-flex align-items-end mb-3">
                    <button
                      type="button"
                      className="ff-btn ff-btn--secondary"
                      onClick={handleFindHubs}
                      disabled={isLoadingHubs}
                    >
                      {isLoadingHubs ? 'Loading...' : 'Find Hubs'}
                    </button>
                  </Col>
                </Row>

                {showHubOptions && hubZip && (
                  <Form.Group className="mb-3">
                    <Form.Label>Select FedEx Hub Near {hubZip}</Form.Label>
                    <Form.Select
                      value={selectedHub}
                      onChange={(e) => setSelectedHub(e.target.value)}
                      required
                    >
                      <option value="">-- Select a Hub --</option>
                      {hubOptions.map((hub, index) => {
                        const addr = hub.contactAndAddress?.address;
                        const distance = hub.distance?.value?.toFixed(1);
                        const name = hub.contactAndAddress?.contact?.companyName || 'FedEx Hub';
                        const street = addr?.streetLines?.[0] || '';
                        const city = addr?.city || '';
                        const state = addr?.stateOrProvinceCode || '';
                        const zip = addr?.postalCode || '';
                        const label = `${distance} mi — ${name} (${street}, ${city}, ${state} ${zip})`;

                        return (
                          <option key={index} value={hub.locationId || index}>
                            {label}
                          </option>
                        );
                      })}
                    </Form.Select>
                  </Form.Group>
                )}
              </div>
            )}

            {/* Show shipping address section only if shipToHub is false (home delivery) */}
            {/* Skip address validation for FedEx hub or store pickup addresses */}
            {!shipToHub && (
              <>
                <div className="address-col">
                  <Form.Group className="mb-3">
                    <Form.Check
                      type="checkbox"
                      label="Shipping address is same as billing"
                      checked={billingSameAsShipping}
                      onChange={() => {
                        const newValue = !billingSameAsShipping;
                        setBillingSameAsShipping(newValue);
                      }}
                    />
                  </Form.Group>
                </div>

                {!billingSameAsShipping && (
                  <div className="address-col mb-4" data-address-section="shipping">
                    <h5 className="checkout-subheading">Shipping Address</h5>
                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>First Name</Form.Label>
                          <Form.Control
                            name="first_name"
                            value={shippingAddress.first_name || ''}
                            onChange={(e) => handleChange(e, setShippingAddress, shippingAddress)}
                            onBlur={(e) => handleBlur('first_name', e.target.value, setShippingErrors, false)}
                            isInvalid={!!shippingErrors.first_name}
                            required
                          />
                          <Form.Control.Feedback type="invalid">
                            {shippingErrors.first_name}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Last Name</Form.Label>
                          <Form.Control
                            name="last_name"
                            value={shippingAddress.last_name || ''}
                            onChange={(e) => handleChange(e, setShippingAddress, shippingAddress)}
                            onBlur={(e) => handleBlur('last_name', e.target.value, setShippingErrors, false)}
                            isInvalid={!!shippingErrors.last_name}
                            required
                          />
                          <Form.Control.Feedback type="invalid">
                            {shippingErrors.last_name}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                    </Row>
                    <Form.Group className="mb-3">
                      <Form.Label>Company (Optional)</Form.Label>
                      <Form.Control
                        name="company"
                        value={shippingAddress.company || ''}
                        onChange={(e) => handleChange(e, setShippingAddress, shippingAddress)}
                        onBlur={(e) => handleBlur('company', e.target.value, setShippingErrors, false)}
                        isInvalid={!!shippingErrors.company}
                      />
                      <Form.Control.Feedback type="invalid">
                        {shippingErrors.company}
                      </Form.Control.Feedback>
                    </Form.Group>
                    <Form.Group className="mb-3">
                      <Form.Label>Address Line 1</Form.Label>
                      <Form.Control
                        name="address_line1"
                        value={shippingAddress.address_line1}
                        onChange={(e) => handleChange(e, setShippingAddress, shippingAddress)}
                        onBlur={(e) => handleBlur('address_line1', e.target.value, setShippingErrors, false)}
                        isInvalid={!!shippingErrors.address_line1}
                        required
                      />
                      <Form.Control.Feedback type="invalid">
                        {shippingErrors.address_line1}
                      </Form.Control.Feedback>
                    </Form.Group>
                    <Form.Group className="mb-3">
                      <Form.Label>Address Line 2 (Optional)</Form.Label>
                      <Form.Control
                        name="address_line2"
                        value={shippingAddress.address_line2}
                        onChange={(e) => handleChange(e, setShippingAddress, shippingAddress)}
                        onBlur={(e) => handleBlur('address_line2', e.target.value, setShippingErrors, false)}
                        isInvalid={!!shippingErrors.address_line2}
                      />
                      <Form.Control.Feedback type="invalid">
                        {shippingErrors.address_line2}
                      </Form.Control.Feedback>
                    </Form.Group>
                    <Row>
                      <Col md={5}>
                        <Form.Group className="mb-3">
                          <Form.Label>City</Form.Label>
                          <Form.Control
                            name="city"
                            value={shippingAddress.city}
                            onChange={(e) => handleChange(e, setShippingAddress, shippingAddress)}
                            onBlur={(e) => handleBlur('city', e.target.value, setShippingErrors, false)}
                            isInvalid={!!shippingErrors.city}
                            required
                          />
                          <Form.Control.Feedback type="invalid">
                            {shippingErrors.city}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                      <Col md={4}>
                        <Form.Group className="mb-3">
                          <Form.Label>State</Form.Label>
                          <Form.Select
                            name="state"
                            value={shippingAddress.state}
                            onChange={(e) => handleChange(e, setShippingAddress, shippingAddress)}
                            onBlur={(e) => handleBlur('state', e.target.value, setShippingErrors, false)}
                            isInvalid={!!shippingErrors.state}
                            required
                          >
                            <option value="">Select a state</option>
                            {states.map((stateName) => (
                              <option key={stateName} value={stateName}>{stateName}</option>
                            ))}
                          </Form.Select>
                          <Form.Control.Feedback type="invalid">
                            {shippingErrors.state}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                      <Col md={3}>
                        <Form.Group className="mb-3">
                          <Form.Label>Zip Code</Form.Label>
                          <Form.Control
                            name="zip"
                            value={shippingAddress.zip}
                            onChange={(e) => handleChange(e, setShippingAddress, shippingAddress)}
                            onBlur={(e) => handleBlur('zip', e.target.value, setShippingErrors, false)}
                            isInvalid={!!shippingErrors.zip}
                            placeholder="64105"
                            maxLength={5}
                            required
                          />
                          <Form.Control.Feedback type="invalid">
                            {shippingErrors.zip}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                    </Row>
                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Phone</Form.Label>
                          <Form.Control
                            type="tel"
                            name="phone"
                            value={shippingAddress.phone}
                            onChange={(e) => handleChange(e, setShippingAddress, shippingAddress)}
                            onBlur={(e) => handleBlur('phone', e.target.value, setShippingErrors, false)}
                            isInvalid={!!shippingErrors.phone}
                            placeholder="(816) 555-1234"
                            title="Please enter a valid phone number so the carrier can reach you if needed."
                            required
                          />
                          <Form.Control.Feedback type="invalid">
                            {shippingErrors.phone}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                    </Row>
                  </div>
                )}
              </>
            )}

            {/* #65: sticky wrapper keeps the CTA on screen through the
                whole form (it previously sat below the fold with no
                scroll assistance). */}
            <div className="checkout-submit-bar">
              {blockedMessage && (
                <Alert
                  variant="warning"
                  className="mb-2"
                  role="alert"
                  data-testid="checkout-blocked-message"
                >
                  {blockedMessage}
                </Alert>
              )}
              {/* Design System v1 §2.2 (Phase 3): the step's ONE primary is
                  Proceed to Payment; the way back is a ghost. */}
              <div className="checkout-submit-actions">
                <Link to="/cart" className="ff-btn ff-btn--ghost checkout-back">
                  ← Back to cart
                </Link>
                <button
                  type="submit"
                  className="ff-btn ff-btn--primary checkout-submit"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Creating Order...' : 'Proceed to Payment'}
                </button>
              </div>
            </div>
          </div>
        </Form>

        {/* Weather-safety trust block (customer slice 2026-08) */}
        <WeatherTrustBlock />
      </Container>
    </div>
  );
};

export default CheckoutAddressPage;